/**
 * metaService.js — cliente del módulo de Integración con Meta (API oficial).
 *
 * Pega contra los endpoints /api/meta/* del backend. El token de System User se
 * captura una sola vez desde la pantalla de administración y vive cifrado en el
 * backend; aquí NUNCA se maneja el token de vuelta (solo estado/IDs enmascarados).
 */

const API = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

async function jsonFetch(url, options = {}) {
  const res = await fetch(url, { cache: 'no-store', ...options });
  let data = {};
  try { data = await res.json(); } catch (_) { /* sin cuerpo */ }
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.code = data.code;
    throw err;
  }
  return data;
}

const qs = (obj) =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

const postJson = (url, body) => jsonFetch(url, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export const metaApi = {
  // ── Configuración (solo admin) ──
  getConfig: (username) =>
    jsonFetch(`${API}/api/meta/config?${qs({ username })}`),

  saveConfig: (username, { accessToken, adAccountId, pageId, igUserId }) =>
    postJson(`${API}/api/meta/config`, {
      username,
      access_token: accessToken,
      ad_account_id: adAccountId,
      page_id: pageId,
      ig_user_id: igUserId,
    }),

  testConfig: (username) =>
    postJson(`${API}/api/meta/config/test`, { username }),

  deleteConfig: (username) =>
    jsonFetch(`${API}/api/meta/config?${qs({ username })}`, { method: 'DELETE' }),

  // ── Credenciales adicionales (otro System User, otro portafolio) ──
  // Solo para cuentas publicitarias; lo organico usa la credencial principal.
  listCredentials: (username) =>
    jsonFetch(`${API}/api/meta/credentials?${qs({ username })}`),

  saveCredential: (username, { label, accessToken, adAccountId }) =>
    postJson(`${API}/api/meta/credentials`, {
      username, label, access_token: accessToken, ad_account_id: adAccountId,
    }),

  deleteCredential: (username, label) =>
    jsonFetch(`${API}/api/meta/credentials?${qs({ username, label })}`, { method: 'DELETE' }),

  // ── Datos ──
  // Cuentas publicitarias dadas de alta para la organizacion (alimenta el
  // selector del tablero cuando hay mas de una: la propia y la de la agencia).
  adsAccounts: (username) =>
    jsonFetch(`${API}/api/meta/ads/accounts?${qs({ username })}`),

  // `fresh` salta la caché de 10 minutos del backend (botón Actualizar).
  adsInsights: (username, dateFrom, dateTo, level = 'campaign', account, fresh) =>
    jsonFetch(`${API}/api/meta/ads/insights?${qs({ username, date_from: dateFrom, date_to: dateTo, level, account, fresh: fresh ? 1 : null })}`),

  // Desgloses de Ads Insights (serie diaria, audiencia, ubicaciones, anuncios,
  // salud de la entrega). `campaign` + `result` los acotan a una campaña.
  // Rango por defecto en el backend: 12 meses.
  adsExplore: (username, dateFrom, dateTo, account, { campaign, result, fresh } = {}) =>
    jsonFetch(`${API}/api/meta/ads/explore?${qs({ username, date_from: dateFrom, date_to: dateTo, account, campaign, result, fresh: fresh ? 1 : null })}`),

  // Drill-down de una campaña: ficha, conjuntos de anuncios y anuncios.
  adsCampaign: (username, campaign, dateFrom, dateTo, account, fresh) =>
    jsonFetch(`${API}/api/meta/ads/campaign?${qs({ username, campaign, date_from: dateFrom, date_to: dateTo, account, fresh: fresh ? 1 : null })}`),

  // Diagnostico: meses que reporta la cuenta vs los que reportan las campanas,
  // mas las cuentas publicitarias que alcanza el token. Para cotejar cuando el
  // tablero muestra menos meses de los esperados.
  adsDiagnostics: (username, dateFrom, dateTo, account) =>
    jsonFetch(`${API}/api/meta/ads/diagnostics?${qs({ username, date_from: dateFrom, date_to: dateTo, account })}`),

  // Orgánico: un endpoint por red. Instagram y Facebook son pestañas distintas de
  // Redes Orgánicas (/social-organic) aunque compartan el token de Meta.
  organicInstagram: (username, dateFrom, dateTo) =>
    jsonFetch(`${API}/api/meta/organic/instagram?${qs({ username, date_from: dateFrom, date_to: dateTo })}`),

  organicFacebook: (username, dateFrom, dateTo) =>
    jsonFetch(`${API}/api/meta/organic/facebook?${qs({ username, date_from: dateFrom, date_to: dateTo })}`),

  // Instagram, bajo demanda: historias del periodo (guardadas en BD; `refreshing`
  // = hay un refresco en curso), menciones de terceros y el detalle de una
  // publicación al abrirla.
  organicInstagramStories: (username, dateFrom, dateTo) =>
    jsonFetch(`${API}/api/meta/organic/instagram/stories?${qs({ username, date_from: dateFrom, date_to: dateTo })}`),

  organicInstagramTags: (username) =>
    jsonFetch(`${API}/api/meta/organic/instagram/tags?${qs({ username })}`),

  // Altas/bajas de seguidores por mes (últimos 12 meses), IG o FB.
  organicFollowersMonthly: (username, network) =>
    jsonFetch(`${API}/api/meta/organic/followers-monthly?${qs({ username, network })}`),

  organicMediaDetail: (username, mediaId, productType) =>
    jsonFetch(`${API}/api/meta/organic/instagram/media-detail?${qs({ username, media_id: mediaId, product_type: productType })}`),

  // LEGACY: IG + Página juntos. Ningún tablero lo usa ya; se mantiene por si algo
  // externo sigue pegándole (el snapshot a S3 llama al cliente Python, no a esto).
  organicOverview: (username, dateFrom, dateTo) =>
    jsonFetch(`${API}/api/meta/organic/overview?${qs({ username, date_from: dateFrom, date_to: dateTo })}`),

  // Comentarios de una publicación propia (bajo demanda, API oficial).
  organicComments: (username, mediaId, network = 'instagram') =>
    jsonFetch(`${API}/api/meta/organic/comments?${qs({ username, media_id: mediaId, network })}`),
};

export default metaApi;
